import React, { useState, useEffect } from 'react';
import { Server, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, ChevronUp, Loader2, Heart, Activity, Info, Network, Box, Cpu, Database, Bell, Clock, Eye, EyeOff, Check, Columns3 } from 'lucide-react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { useNavigate } from 'react-router-dom';
import baseapi from '../../services/baseapi';
import { showError } from '../../utils/toast';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';

export type ClusterColumnKey =
    | 'name'
    | 'health'
    | 'status'
    | 'kubernetes_version'
    | 'nodes_total'
    | 'pods_total'
    | 'cpu_pct'
    | 'mem_pct'
    | 'alerts_total'
    | 'last_seen';

export const defaultClusterVisibleColumns: Record<ClusterColumnKey, boolean> = {
    name: true,
    health: true,
    status: true,
    kubernetes_version: true,
    nodes_total: true,
    pods_total: true,
    cpu_pct: true,
    mem_pct: true,
    alerts_total: true,
    last_seen: true,
};

export const clusterColumnOptions: { key: ClusterColumnKey; label: string }[] = [
    { key: 'name', label: 'Cluster Name' },
    { key: 'health', label: 'Health' },
    { key: 'status', label: 'Status' },
    { key: 'kubernetes_version', label: 'Version' },
    { key: 'nodes_total', label: 'Nodes' },
    { key: 'pods_total', label: 'Pods' },
    { key: 'cpu_pct', label: 'CPU' },
    { key: 'mem_pct', label: 'Memory' },
    { key: 'alerts_total', label: 'Alerts' },
    { key: 'last_seen', label: 'Last Seen' },
];

interface KubernetesClustersTableProps {
    clientId: string | number;
    searchTerm: string;
    refreshTrigger?: number;
    onLoadingChange?: (loading: boolean) => void;
    visibleColumns?: Record<ClusterColumnKey, boolean>;
    showColumnButton?: boolean;
}

interface ClusterData {
    id: number;
    agent_id?: number;
    name: string;
    client_id: number;
    organization_id: number;
    agent_type: string;
    status: string;
    health: string;
    last_seen: string;
    kubernetes_version: string;
    platform: string;
    nodes_total: number;
    nodes_ready: number;
    nodes_with_pressure: number;
    pods_total: number;
    pods_healthy: number;
    pods_failed: number;
    pods_pending: number;
    pods_with_restarts: number;
    problem_pods_count: number;
    total_restarts: number;
    deployments_total: number;
    deployments_healthy: number;
    daemonsets_total: number;
    daemonsets_healthy: number;
    statefulsets_total: number;
    statefulsets_healthy: number;
    jobs_total: number;
    jobs_failed: number;
    jobs_healthy: number;
    cronjobs_total: number;
    cronjobs_suspended: number;
    cpu_pct: number;
    cpu_used_m: number;
    cpu_alloc_m: number;
    mem_pct: number;
    mem_used_bytes: number;
    mem_alloc_bytes: number;
    fs_pct: number;
    fs_used_bytes: number;
    fs_capacity_bytes: number;
    network_rx_bytes: number;
    network_tx_bytes: number;
    network_total_bytes: number;
    namespaces_total: number;
    services_total: number;
    services_loadbalancer: number;
    pvcs_total: number;
    pvcs_bound: number;
    pvcs_pending: number;
    warning_events_count: number;
    top_warning_reasons: string[];
    alerts_total: number;
    alerts_critical: number;
    alerts_warning: number;
    metrics_server_available: boolean;
    yaml_url: string;
}

const CpuProgress: React.FC<{ pct: number }> = ({ pct }) => {
    const cleanPct = Math.min(100, Math.max(0, pct ?? 0));
    const radius = 17;
    const strokeWidth = 4;
    const circumference = 2 * Math.PI * radius; // ~106.81
    const strokeDashoffset = circumference - (cleanPct / 100) * circumference;

    let color = 'stroke-green-500';
    if (cleanPct >= 85) color = 'stroke-red-500';
    else if (cleanPct >= 70) color = 'stroke-yellow-500';

    return (
        <div className="flex items-center justify-center">
            <div className="relative flex items-center justify-center w-11 h-11">
                <svg viewBox="0 0 44 44" className="w-full h-full transform -rotate-90">
                    {/* Background circle */}
                    <circle
                        cx="22"
                        cy="22"
                        r={radius}
                        className="stroke-gray-700/50 fill-none"
                        strokeWidth={strokeWidth}
                    />
                    {/* Progress circle */}
                    <circle
                        cx="22"
                        cy="22"
                        r={radius}
                        className={`${color} fill-none transition-all duration-300`}
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                    />
                </svg>
                <span className="absolute text-[8.5px] font-bold font-mono text-gray-200">
                    {cleanPct}%
                </span>
            </div>
        </div>
    );
};

const MemoryProgress: React.FC<{ pct: number }> = ({ pct }) => {
    const cleanPct = Math.min(100, Math.max(0, pct ?? 0));

    let color = 'bg-purple-500';
    if (cleanPct >= 85) color = 'bg-red-500';
    else if (cleanPct >= 70) color = 'bg-yellow-500';

    return (
        <div className="flex flex-col items-center justify-center gap-1 min-w-[70px]">
            <span className="text-[10px] font-bold font-mono text-gray-200">{cleanPct}%</span>
            <div className="w-16 h-1.5 bg-gray-700/50 rounded-full overflow-hidden">
                <div
                    className={`${color} h-full rounded-full transition-all duration-300`}
                    style={{ width: `${cleanPct}%` }}
                />
            </div>
        </div>
    );
};

const KubernetesClustersTable: React.FC<KubernetesClustersTableProps> = ({
    clientId,
    searchTerm,
    refreshTrigger,
    onLoadingChange,
    visibleColumns,
    showColumnButton = false,
}) => {
    const navigate = useNavigate();
    const [clusters, setClusters] = useState<ClusterData[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(30);
    const [sortConfig, setSortConfig] = useState<{ key: keyof ClusterData; direction: 'asc' | 'desc' } | null>(null);

    // Internal column filter state if not passed from parent
    const [internalVisibleColumns, setInternalVisibleColumns] = useState<Record<ClusterColumnKey, boolean>>(defaultClusterVisibleColumns);
    const [showColumnFilter, setShowColumnFilter] = useState(false);

    const activeVisibleColumns = visibleColumns || internalVisibleColumns;

    const handleColumnToggle = (column: ClusterColumnKey) => {
        setInternalVisibleColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    };

    const fetchClusters = async () => {
        try {
            setLoading(true);
            if (onLoadingChange) onLoadingChange(true);
            const response = await baseapi.get('k8s_clusters/', {
                params: { client_id: clientId }
            });
            if (response.data?.status) {
                setClusters(response.data.data || []);
            } else {
                setClusters([]);
            }
        } catch (error: any) {
            console.error("Error fetching clusters:", error);
            showError(error.message || "Failed to fetch Kubernetes clusters.");
            setClusters([]);
        } finally {
            setLoading(false);
            if (onLoadingChange) onLoadingChange(false);
        }
    };

    useEffect(() => {
        fetchClusters();
    }, [clientId, refreshTrigger]);

    const handleSort = (key: keyof ClusterData) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Filter clusters client side
    const filteredClusters = clusters.filter(cluster =>
        cluster.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cluster.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cluster.health.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort clusters
    const sortedClusters = [...filteredClusters];
    if (sortConfig) {
        sortedClusters.sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            if (aVal === undefined || aVal === null) return 1;
            if (bVal === undefined || bVal === null) return -1;

            if (aVal < bVal) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aVal > bVal) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    // Pagination
    const totalItems = sortedClusters.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedClusters = sortedClusters.slice(startIndex, endIndex);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(1);
    };

    const getStatusColor = (status: string) => {
        return status?.toLowerCase() === 'online' ? 'text-green-400' : 'text-red-500';
    };

    const getStatusBg = (status: string) => {
        return status?.toLowerCase() === 'online' ? 'bg-green-400' : 'bg-red-500';
    };

    const getHealthStyle = (health: string) => {
        switch (health?.toUpperCase()) {
            case 'HEALTHY':
                return 'bg-green-500/10 text-green-400 border border-green-500/20';
            case 'WARNING':
                return 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20';
            case 'CRITICAL':
            default:
                return 'bg-red-500/10 text-red-400 border border-red-500/20';
        }
    };

    const renderSortArrow = (key: keyof ClusterData) => {
        if (sortConfig?.key === key) {
            return sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
        }
        return <ChevronDown size={12} className="opacity-30" />;
    };

    const visibleColsCount = Object.values(activeVisibleColumns).filter(Boolean).length || 1;

    return (
        <div className="w-full mx-auto">
            {showColumnButton && (
                <div className="flex justify-end mb-2">
                    <div className="relative" data-col-menu>
                        <button
                            onClick={() => setShowColumnFilter(!showColumnFilter)}
                            className="p-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 text-gray-200 transition-all flex items-center gap-2"
                        >
                            <Columns3 className="w-3.5 h-3.5" />
                            <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                        </button>
                        {showColumnFilter && (
                            <div className="absolute top-full mt-2 right-0 w-52 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto">
                                <div className="px-3 py-1.5 text-[9px] font-bold text-gray-500 uppercase border-b border-gray-800 mb-1">Toggle Columns</div>
                                {clusterColumnOptions.map((option) => (
                                    <button
                                        key={option.key}
                                        onClick={() => handleColumnToggle(option.key)}
                                        className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-400 hover:bg-gray-800 rounded-lg transition-colors"
                                    >
                                        <div className="flex items-center gap-2.5">
                                            {activeVisibleColumns[option.key] ? (
                                                <Eye className="w-3 h-3 text-orange-500" />
                                            ) : (
                                                <EyeOff className="w-3 h-3 text-gray-600" />
                                            )}
                                            {option.label}
                                        </div>
                                        <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${activeVisibleColumns[option.key] ? 'bg-orange-600 border-orange-600' : 'border-gray-700'}`}>
                                            {activeVisibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Clusters Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-t-xl overflow-hidden shadow-2xl">
                <ScrollContainer
                    className="overflow-x-auto custom-scroll cursor-grab active:cursor-grabbing"
                    horizontal={true}
                    vertical={false}
                    hideScrollbars={false}
                >
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                {activeVisibleColumns.name && (
                                    <th onClick={() => handleSort('name')} className="px-3 py-3 text-[10px] font-bold text-gray-100 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer">
                                        <div className="flex items-center gap-1">
                                            <Server className="w-3.5 h-3.5 text-blue-400" />
                                            Cluster Name {renderSortArrow('name')}
                                        </div>
                                    </th>
                                )}
                                {activeVisibleColumns.health && (
                                    <th onClick={() => handleSort('health')} className="px-3 py-3 text-[10px] font-bold text-gray-100 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer text-center">
                                        <div className="flex items-center gap-1 justify-center">
                                            <Heart className="w-3.5 h-3.5 text-red-400" />
                                            Health {renderSortArrow('health')}
                                        </div>
                                    </th>
                                )}
                                {activeVisibleColumns.status && (
                                    <th onClick={() => handleSort('status')} className="px-3 py-3 text-[10px] font-bold text-gray-100 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer text-center">
                                        <div className="flex items-center gap-1 justify-center">
                                            <Activity className="w-3.5 h-3.5 text-emerald-400" />
                                            Status {renderSortArrow('status')}
                                        </div>
                                    </th>
                                )}
                                {activeVisibleColumns.kubernetes_version && (
                                    <th onClick={() => handleSort('kubernetes_version')} className="px-3 py-3 text-[10px] font-bold text-gray-100 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer text-center">
                                        <div className="flex items-center gap-1 justify-center">
                                            <Info className="w-3.5 h-3.5 text-cyan-400" />
                                            Version {renderSortArrow('kubernetes_version')}
                                        </div>
                                    </th>
                                )}
                                {activeVisibleColumns.nodes_total && (
                                    <th onClick={() => handleSort('nodes_total')} className="px-3 py-3 text-[10px] font-bold text-gray-100 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer text-center">
                                        <div className="flex items-center gap-1 justify-center">
                                            <Network className="w-3.5 h-3.5 text-indigo-400" />
                                            Nodes {renderSortArrow('nodes_total')}
                                        </div>
                                    </th>
                                )}
                                {activeVisibleColumns.pods_total && (
                                    <th onClick={() => handleSort('pods_total')} className="px-3 py-3 text-[10px] font-bold text-gray-100 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer text-center">
                                        <div className="flex items-center gap-1 justify-center">
                                            <Box className="w-3.5 h-3.5 text-orange-400" />
                                            Pods {renderSortArrow('pods_total')}
                                        </div>
                                    </th>
                                )}
                                {activeVisibleColumns.cpu_pct && (
                                    <th onClick={() => handleSort('cpu_pct')} className="px-3 py-3 text-[10px] font-bold text-gray-100 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer text-center">
                                        <div className="flex items-center gap-1 justify-center">
                                            <Cpu className="w-3.5 h-3.5 text-pink-400" />
                                            CPU {renderSortArrow('cpu_pct')}
                                        </div>
                                    </th>
                                )}
                                {activeVisibleColumns.mem_pct && (
                                    <th onClick={() => handleSort('mem_pct')} className="px-3 py-3 text-[10px] font-bold text-gray-100 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer text-center">
                                        <div className="flex items-center gap-1 justify-center">
                                            <Database className="w-3.5 h-3.5 text-purple-400" />
                                            Memory {renderSortArrow('mem_pct')}
                                        </div>
                                    </th>
                                )}
                                {activeVisibleColumns.alerts_total && (
                                    <th onClick={() => handleSort('alerts_total')} className="px-3 py-3 text-[10px] font-bold text-gray-100 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer text-center">
                                        <div className="flex items-center gap-1 justify-center">
                                            <Bell className="w-3.5 h-3.5 text-yellow-400" />
                                            Alerts {renderSortArrow('alerts_total')}
                                        </div>
                                    </th>
                                )}
                                {activeVisibleColumns.last_seen && (
                                    <th onClick={() => handleSort('last_seen')} className="px-3 py-3 text-[10px] font-bold text-gray-100 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer text-center">
                                        <div className="flex items-center gap-1 justify-center">
                                            <Clock className="w-3.5 h-3.5 text-blue-400" />
                                            Last Seen {renderSortArrow('last_seen')}
                                        </div>
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {paginatedClusters.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="py-16 text-center text-gray-100 font-bold uppercase tracking-widest italic">
                                        No clusters found
                                    </td>
                                </tr>
                            ) : loading ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="py-16 text-center text-gray-200 font-bold uppercase tracking-widest italic">
                                        <div className="flex items-center justify-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                                            Loading clusters...
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                paginatedClusters.map((cluster) => (
                                    <tr key={cluster.id} className="hover:bg-gray-800/60 transition-colors group border-b border-gray-700/30">
                                        {/* Cluster Name */}
                                        {activeVisibleColumns.name && (
                                            <td className="px-3 py-2.5 font-bold text-gray-200">
                                                <button
                                                    onClick={() => navigate(`/kubernetes/dashboard/${cluster.agent_id || cluster.id}?client_id=${cluster.client_id}&cluster_name=${cluster.name}`)}
                                                    className="text-blue-500 hover:underline cursor-pointer text-left font-bold"
                                                >
                                                    {cluster.name || '-'}
                                                </button>
                                            </td>
                                        )}

                                        {/* Health */}
                                        {activeVisibleColumns.health && (
                                            <td className="px-3 py-2.5 text-center">
                                                <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-[9px] font-extrabold tracking-wider ${getHealthStyle(cluster.health)}`}>
                                                    {cluster.health || 'UNKNOWN'}
                                                </span>
                                            </td>
                                        )}

                                        {/* Status */}
                                        {activeVisibleColumns.status && (
                                            <td className="px-3 py-2.5 text-center">
                                                <div className="inline-flex items-center justify-center gap-1.5 px-2.5 py-0.5 rounded-full border border-gray-700/50 bg-gray-900/50">
                                                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusBg(cluster.status)}`}></div>
                                                    <span className={`text-[8px] font-bold uppercase tracking-wide ${getStatusColor(cluster.status)}`}>
                                                        {cluster.status || '-'}
                                                    </span>
                                                </div>
                                            </td>
                                        )}

                                        {/* Kubernetes Version */}
                                        {activeVisibleColumns.kubernetes_version && (
                                            <td className="px-3 py-2.5 text-center text-gray-300 font-mono text-[10px]">
                                                {cluster.kubernetes_version || '-'}
                                            </td>
                                        )}

                                        {/* Nodes */}
                                        {activeVisibleColumns.nodes_total && (
                                            <td className="px-3 py-2.5 text-center text-gray-200 font-bold font-mono">
                                                {cluster.nodes_total ?? 0}
                                            </td>
                                        )}

                                        {/* Pods */}
                                        {activeVisibleColumns.pods_total && (
                                            <td className="px-3 py-2.5 text-center text-gray-200 font-bold font-mono">
                                                {cluster.pods_total ?? 0}
                                            </td>
                                        )}

                                        {/* CPU Percentage */}
                                        {activeVisibleColumns.cpu_pct && (
                                            <td className="px-3 py-2.5 text-center font-bold font-mono">
                                                <CpuProgress pct={cluster.cpu_pct} />
                                            </td>
                                        )}

                                        {/* Memory Percentage */}
                                        {activeVisibleColumns.mem_pct && (
                                            <td className="px-3 py-2.5 text-center font-bold font-mono">
                                                <MemoryProgress pct={cluster.mem_pct} />
                                            </td>
                                        )}

                                        {/* Alerts */}
                                        {activeVisibleColumns.alerts_total && (
                                            <td className="px-3 py-2.5 text-center text-gray-200 font-bold">
                                                {cluster.alerts_total ?? 0}
                                            </td>
                                        )}

                                        {/* Last Seen */}
                                        {activeVisibleColumns.last_seen && (
                                            <td className="px-3 py-2.5 text-center text-gray-300 font-mono text-[10px] whitespace-nowrap">
                                                {cluster.last_seen ? new Date(cluster.last_seen).toLocaleString() : '—'}
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </ScrollContainer>
            </div>

            {/* Pagination Footer */}
            <div className="bg-gray-800/50 border rounded-b-xl border-gray-700 px-2 py-1 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <ItemsPerPageDropdown
                        value={itemsPerPage}
                        onChange={(val: any) => handleItemsPerPageChange(val)}
                    />
                    <span className="text-[10px] text-gray-100 font-bold uppercase tracking-widest">
                        <span className="text-gray-200">{startIndex + 1}</span> - <span className="text-gray-200">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30"><ChevronsLeft className="w-4 h-4" /></button>
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                    <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">{currentPage}</div>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                    <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30"><ChevronsRight className="w-4 h-4" /></button>
                </div>
            </div>
        </div>
    );
};

export default KubernetesClustersTable;
